import React, { useEffect, useRef, useState } from "react";
import { Translate, Check, CaretDown } from "@phosphor-icons/react";

// 22 curated major music-market languages.
const LANGUAGES = [
  { code: "en", name: "English", native: "English" },
  { code: "es", name: "Spanish", native: "Español" },
  { code: "pt", name: "Portuguese", native: "Português" },
  { code: "fr", name: "French", native: "Français" },
  { code: "de", name: "German", native: "Deutsch" },
  { code: "it", name: "Italian", native: "Italiano" },
  { code: "nl", name: "Dutch", native: "Nederlands" },
  { code: "ru", name: "Russian", native: "Русский" },
  { code: "ja", name: "Japanese", native: "日本語" },
  { code: "ko", name: "Korean", native: "한국어" },
  { code: "zh-CN", name: "Chinese", native: "中文 (简体)" },
  { code: "zh-TW", name: "Chinese Traditional", native: "中文 (繁體)" },
  { code: "ar", name: "Arabic", native: "العربية" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "bn", name: "Bengali", native: "বাংলা" },
  { code: "ur", name: "Urdu", native: "اردو" },
  { code: "vi", name: "Vietnamese", native: "Tiếng Việt" },
  { code: "tl", name: "Filipino", native: "Tagalog" },
  { code: "tr", name: "Turkish", native: "Türkçe" },
  { code: "pl", name: "Polish", native: "Polski" },
  { code: "id", name: "Indonesian", native: "Bahasa Indonesia" },
  { code: "th", name: "Thai", native: "ไทย" },
];

function getCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
}

function getCurrentLang() {
  const g = getCookie("googtrans"); // "/en/es"
  if (g) {
    const parts = g.split("/");
    if (parts.length >= 3 && parts[2]) return parts[2];
  }
  return "en";
}

function setLang(code) {
  // Set/clear cookie for both host + parent domain so it survives www/apex variants.
  const host = window.location.hostname;
  const parent = host.split(".").slice(-2).join(".");
  const paths = ["/", "/admin"];
  const domains = new Set(["", host, "." + parent]);

  // ALWAYS clear existing googtrans first (so "switch back to English" works).
  const past = "; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  for (const dom of domains) {
    const dpart = dom ? `; domain=${dom}` : "";
    for (const p of paths) document.cookie = `googtrans=${past}; path=${p}${dpart}`;
  }

  if (code !== "en") {
    const expires = "; expires=" + new Date(Date.now() + 365 * 86400e3).toUTCString();
    for (const dom of domains) {
      const dpart = dom ? `; domain=${dom}` : "";
      for (const p of paths) document.cookie = `googtrans=/en/${code}; path=${p}${dpart}${expires}`;
    }
  }
  window.location.reload();
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);
  const initedRef = useRef(false);

  useEffect(() => { setCurrent(getCurrentLang()); }, []);

  // Mount the (hidden) Google Translate runtime once so cookie-based selections take effect.
  useEffect(() => {
    if (initedRef.current || typeof window === "undefined") return;
    initedRef.current = true;
    window.googleTranslateElementInit = () => {
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement(
        { pageLanguage: "en", autoDisplay: false }, "gt-hidden"
      );
    };
    if (!document.getElementById("gt-script")) {
      const s = document.createElement("script");
      s.id = "gt-script";
      s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div className="relative notranslate" translate="no" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-ink-600 bg-ink-800/60 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 transition-colors text-xs font-mono tracking-widest uppercase notranslate"
        translate="no"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-button"
      >
        <Translate size={14} weight="bold" />
        <span className="hidden sm:inline">{currentLang.native}</span>
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto glass rounded-lg border border-neon-cyan/20 p-1.5 z-50 scrollbar-thin notranslate"
          translate="no"
          data-testid="language-menu"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === current}
              onClick={() => setLang(l.code)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded text-sm transition-colors ${
                l.code === current ? "text-neon-cyan bg-ink-700/40" : "text-zinc-200 hover:bg-ink-700/30 hover:text-white"
              }`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="flex flex-col items-start">
                <span className="font-medium">{l.native}</span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{l.name}</span>
              </span>
              {l.code === current && <Check size={14} weight="bold" />}
            </button>
          ))}
        </div>
      )}
      {/* Hidden Google Translate runtime — required for cookie-based switching to apply */}
      <div id="gt-hidden" style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden" }} />
      <style>{`
        body { top: 0 !important; }
        .goog-te-banner-frame, .skiptranslate, .goog-logo-link, .goog-te-gadget > span { display: none !important; }
        font, font:hover { background: transparent !important; box-shadow: none !important; }
      `}</style>
    </div>
  );
}
